import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountService from '@/services/AccountService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const loginEmailRef = useRef(null);
    const navigate = useNavigate();

    const loadUser = useCallback(async () => {
        const user = await AccountService.getMe();
        try {
            const profile = await AccountService.getProfile();
            return { ...user, twoFactorEnabled: !!profile?.twoFactorEnabled };
        } catch {
            return user;
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setIsLoading(false);
            return;
        }
        loadUser()
            .then(user => setCurrentUser(user))
            .catch(() => localStorage.removeItem('token'))
            .finally(() => setIsLoading(false));
    }, [loadUser]);

    const login = useCallback(async (email, password) => {
        loginEmailRef.current = email;
        const result = await AccountService.login(email, password);
        if (result?.requiresTwoFactor) {
            return {
                success: false,
                requiresTwoFactor: true,
                pendingToken: result.pendingToken,
                twoFactorMethod: result.twoFactorMethod ?? 'Totp',
            };
        }
        if (result?.token) {
            localStorage.setItem('token', result.token);
            const user = await loadUser();
            setCurrentUser(user);
            return { success: true };
        }
        return { success: false, error: result?.message || 'Invalid email or password' };
    }, [loadUser]);

    const completeTwoFactorLogin = useCallback(async (pendingToken, code) => {
        const result = await AccountService.verifyTwoFactorLogin(pendingToken, code, loginEmailRef.current);
        if (result?.token) {
            localStorage.setItem('token', result.token);
            const user = await loadUser();
            setCurrentUser(user);
            return { success: true };
        }
        return { success: false, error: result?.message || 'Invalid code' };
    }, [loadUser]);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setCurrentUser(null);
        navigate('/login');
    }, [navigate]);

    const refreshUser = useCallback(async () => {
        setCurrentUser(await loadUser());
    }, [loadUser]);

    const updateCurrentUser = useCallback((partial) => {
        setCurrentUser((prev) => (prev ? { ...prev, ...partial } : prev));
    }, []);

    return (
        <AuthContext.Provider value={{
            currentUser,
            isLoading,
            isAuthenticated: !!currentUser,
            isOwner: currentUser?.role === 'Owner' || currentUser?.role === 'Admin',
            login,
            completeTwoFactorLogin,
            logout,
            updateCurrentUser,
            refreshUser,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
